import { useSelector } from "react-redux";
import { useMemo } from "react";
import { checkIsLoading } from "common/utils/checkLoading";
import { turfBounds } from "shared/utils";

import { RootState } from "store";

import { NAVIGATION_STATE } from "../../constants";
import { NavigationHistoryPath, TransactionWithType } from "../../types";

import {
  checkIsUntrackedLocationHistoryPath,
  checkIsParkingLocationHistoryPath,
} from "../../utils";
import { useHistoryLocations } from "entities/navigation/hooks";
import { EMPTY_ARRAY } from "common/constants";

export const useNavigationLocationsHistoryLoadingStatus = () => {
  const navigationLocationsHistoryLoadingStatus = useSelector(
    (state: RootState) => state[NAVIGATION_STATE].history.loadingStatus
  );
  return navigationLocationsHistoryLoadingStatus;
};

export const useIsNavigationLocationsHistoryLoading = () => {
  const navigationLocationsHistoryLoadingStatus =
    useNavigationLocationsHistoryLoadingStatus();
  return checkIsLoading(navigationLocationsHistoryLoadingStatus);
};

export const useNavigationHistory = () => {
  const navigationHistory = useSelector(
    (state: RootState) => state[NAVIGATION_STATE].history.data
  );
  const historyUserLocation = useHistoryLocations();
  return navigationHistory ?? historyUserLocation;
};

export const useNavigationHistoryPath = () => {
  const { path } = useNavigationHistory() || {};
  return path;
};

export const useNavigationHistoryPathCoordinatesByIdex = (index?: number) => {
  const historyPath = useNavigationHistoryPath();

  return useMemo(() => (
    !historyPath || index === undefined || index === -1 ? undefined : historyPath[index]
  ), [index, historyPath]);
};

export const useNavigationHistoryBounds = () => {
  const navigationHistoryPath = useNavigationHistoryPath();

  const navigationHistoryBounds = useMemo(() => {
    const coords = (navigationHistoryPath || []).reduce(
      (acc, { locations }) => {
        locations.forEach(({ latitude, longitude }) => {
          acc.push([longitude, latitude]);
        });
        return acc;
      },
      [] as [number, number][]
    );

    return coords.length ? turfBounds(coords) : null;
  }, [navigationHistoryPath]);

  return navigationHistoryBounds;
};

export const useNavigationUntrackedHistoryPath = () => {
  const navigationHistory = useNavigationHistory();

  const navigationUntrackedHistoryPath = useMemo(
    () =>
      (navigationHistory?.path || []).reduce((acc, item) => {
        if (checkIsUntrackedLocationHistoryPath(item.type)) {
          acc.push(item);
        }
        return acc;
      }, [] as NavigationHistoryPath[]),
    [navigationHistory]
  );

  return navigationUntrackedHistoryPath;
};

export const useNavigationParkingHistoryPath = () => {
  const navigationHistory = useNavigationHistory();

  const navigationParkingHistoryPath = useMemo(
    () =>
      navigationHistory?.path.reduce((acc, item) => {
        if (checkIsParkingLocationHistoryPath(item.type)) {
          acc.push(item);
        }
        return acc;
      }, [] as NavigationHistoryPath[]),
    [navigationHistory]
  );

  return navigationParkingHistoryPath;
};

export const useNavigationAutoObuPairsLoadingStatus = () => {
  const navigationAutoObuPairsLoadingStatus = useSelector(
    (state: RootState) => state[NAVIGATION_STATE].autos.loadingStatus
  );
  return navigationAutoObuPairsLoadingStatus;
};

export const useNavigationAutoObuPairs = () => {
  const navigationAutoObuPairs = useSelector(
    (state: RootState) => state[NAVIGATION_STATE].autos.data
  );
  return navigationAutoObuPairs;
};

export const useNavigationAutoObuPairList = () => {
  const navigationAutoObuPairs = useNavigationAutoObuPairs();

  const navigationAutoObuPairList = useMemo(
    () =>
      (navigationAutoObuPairs || EMPTY_ARRAY).map(({ autoNumber, obuNumber }) => ({
        key: autoNumber,
        obuNumber,
        value: `${autoNumber} (OBU ${obuNumber})`,
      })),
    [navigationAutoObuPairs]
  );

  return navigationAutoObuPairList;
};

export const useClientFuelHistoryEvents = (): TransactionWithType[] => {
  const clientFuelHistoryEvents =
    useSelector(
      (state: RootState) => state[NAVIGATION_STATE].clientEvents.fuel.data
    ) ?? EMPTY_ARRAY;
  return useMemo(() => clientFuelHistoryEvents.map((item) => ({ ...item, type: "Fuel" })), [clientFuelHistoryEvents]);
};

export const useClientHistoryTotalFuelLiters = () => {
  const clientFuelEventsHistory = useClientFuelHistoryEvents();

  return useMemo(() => clientFuelEventsHistory.reduce(
    (acc, { volume }) => acc + volume, 0
  ), [clientFuelEventsHistory]);
};

export const useClientHistoryTotalPriceFuels = () => {
  const clientFuelEventsHistory = useClientFuelHistoryEvents();

  return useMemo(() => clientFuelEventsHistory.reduce(
    (acc, { clientAmount }) => acc + clientAmount, 0
  ), [clientFuelEventsHistory]);
};

export const useClientRoadHistoryEvents = () => {
  const clientRoadHistoryEvents =
    useSelector(
      (state: RootState) => state[NAVIGATION_STATE].clientEvents.road.data
    ) ?? EMPTY_ARRAY;
  return clientRoadHistoryEvents;
};

export const useClientRoadHistoryTollMap = () => {
  const clientTollHistory = useClientRoadHistoryEvents();

  const clientRoadHistoryTollMap = useMemo(() => (
    (clientTollHistory || []).reduce(
      (map, { stationUid, clientAmount, clientCurrency }) => {
        const country = stationUid?.substring(0, 2).toUpperCase() ?? '';
        if (map.has(country)) {
          const value = map.get(country)!;
          map.set(country, {
            ...value,
            clientPrice: value.clientPrice + clientAmount,
          });
        } else {
          map.set(country, { clientPrice: clientAmount, clientCurrency });
        }

        return map;
      },
      new Map<
        string,
        {
          clientPrice: number;
          clientCurrency: Nullable<string>;
        }
      >()
    )
  ), [clientTollHistory]);

  return clientRoadHistoryTollMap;
};

export const useClientRoadHistoryTotalToll = () => {
  const clientRoadHistoryTollMap = useClientRoadHistoryTollMap();

  const clientRoadHistoryTotalToll = useMemo(() => (
    Array.from(clientRoadHistoryTollMap).reduce((acc, [, { clientPrice }]) => (
      acc + Number(clientPrice.toFixed(2))
    ), 0)
  ), [clientRoadHistoryTollMap]);

  return clientRoadHistoryTotalToll;
};

export const useClientRoadHistoryTotal = () => {
  const clientHistoryTotalPriceFuels = useClientHistoryTotalPriceFuels();
  const clientRoadHistoryTotalToll = useClientRoadHistoryTotalToll();

  return clientHistoryTotalPriceFuels + clientRoadHistoryTotalToll;
};

export const useClientServiceHistoryEvents = () => {
  const clientServiceHistoryEvents =
    useSelector(
      (state: RootState) => state[NAVIGATION_STATE].clientEvents.service.data
    ) ?? EMPTY_ARRAY;
  return clientServiceHistoryEvents;
};

export const useClientEventsHistoryFilter = () => {
  const clientEventsPathFilters = useSelector(
    (state: RootState) => state[NAVIGATION_STATE].clientEventsPathFilters
  );
  return clientEventsPathFilters;
};
